/**
 * Cart integrity guard for bundle orders.
 *
 * The warehouse order format uses $0 "(Bundle)" component lines tied to a paid
 * anchor ("box") line via _bundleid. Customers can break that contract by
 * editing the cart (drawer qty bumps, removing the paid box, adding $0
 * duplicate variants directly) — producing free-item orders the fulfillment
 * team must catch by hand.
 *
 * This guard self-heals the cart after any mutation settles:
 *   - $0 (Bundle) lines with no _bundleid, or whose _bundleid has no box
 *     line in the cart, are removed (orphans / hand-added freebies).
 *   - $0 (Bundle) quantities above the box's piece count are trimmed
 *     (box piece count = number of _Piece N properties x box qty).
 *   - Boxes without piece properties (upnova's) only get the orphan check,
 *     never trimming, so mixed/legacy carts are left alone.
 *
 * Safety: upnova and the LC funnels both write the cart in a single atomic
 * add at bundle completion (verified — picks are never staged in the cart),
 * so a debounced sweep cannot touch a mid-build bundle.
 */
(function () {
  'use strict';

  var SWEEP_DEBOUNCE = 4000;
  var timer = null;
  var sweeping = false;

  function isFreeBundleLine(li) {
    return li.final_price === 0 && /\(bundle\)/i.test(li.product_title || li.title || '');
  }

  function sweep() {
    if (sweeping) return;
    sweeping = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var boxAllowance = {}; /* _bundleid -> allowed free items (Infinity = orphan-check only) */
        cart.items.forEach(function (li) {
          var p = li.properties || {};
          if (p._type === 'box' && p._bundleid) {
            var pieces = 0;
            for (var k in p) if (/^_?Piece /.test(k)) pieces++;
            var allow = pieces ? pieces * li.quantity : Infinity;
            boxAllowance[p._bundleid] = (boxAllowance[p._bundleid] || 0) + allow;
          }
        });
        var updates = {};
        var used = {};
        cart.items.forEach(function (li) {
          if (!isFreeBundleLine(li)) return;
          var p = li.properties || {};
          var bid = p._bundleid;
          if (!bid || !(bid in boxAllowance)) { updates[li.key] = 0; return; }
          var left = boxAllowance[bid] - (used[bid] || 0);
          if (li.quantity > left) updates[li.key] = Math.max(0, left);
          used[bid] = (used[bid] || 0) + Math.min(li.quantity, Math.max(0, left));
        });
        if (!Object.keys(updates).length) { sweeping = false; return; }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function () {
          sweeping = false;
          document.dispatchEvent(new CustomEvent('lc:cart-guard-swept'));
          if (/^\/([a-z]{2}(-[a-z]{2})?\/)?cart\/?$/.test(location.pathname)) location.reload();
        });
      })
      .catch(function () { sweeping = false; });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sweep, SWEEP_DEBOUNCE);
  }

  /* re-check after any cart mutation, whoever makes it */
  var origFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = origFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update)(\.js)?([?/]|$)/.test(url)) p.then(schedule, function () {});
    return p;
  };
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (m, url) {
    if (/\/cart\/(add|change|update)(\.js)?([?/]|$)/.test(String(url))) {
      this.addEventListener('loadend', schedule);
    }
    return origOpen.apply(this, arguments);
  };

  /* initial sweep: catches carts assembled on previous pages / direct URL adds */
  setTimeout(sweep, 2500);
})();
